import DateTime from '../lib/DateTime';

/*
|--------------------------------------------------------------------------
| Internal Helpers
|--------------------------------------------------------------------------
*/

function getStartAndEndDates(clinics) {
    let startDate, endDate, timestamp;

    window._.forEach(clinics, clinic => {
        window._.forEach(clinic.Appointments, appointment => {
            timestamp = DateTime.merge(appointment.Date, appointment.Time);

            if (!startDate) {
                startDate = timestamp;
            }

            if (timestamp.isBefore(startDate)) {
                startDate = timestamp;
            }

            if (!endDate) {
                endDate = timestamp;
            }

            if (timestamp.isAfter(endDate)) {
                endDate = timestamp;
            }
        });
    });

    return { startDate, endDate };
}

export function getEarliestAppointment(clinic, future) {
    let i, timestamp, earliest;

    for (i in clinic.Appointments) {
        if (clinic.Appointments.hasOwnProperty(i)) {
            timestamp = DateTime.merge(clinic.Appointments[i].Date, clinic.Appointments[i].Time);

            if (future === true && timestamp.isBefore(window.moment())) {
                continue;
            }

            if (!earliest || timestamp.isBefore(earliest)) {
                earliest = timestamp;
            }
        }
    }

    return earliest;
}

/*
|--------------------------------------------------------------------------
| State
|--------------------------------------------------------------------------
*/

const state = {
    loadingAppointments: false,
    reservingAppointment: false,
    cancellingReservedAppointment: false,
    reservedAppointment: null,
    startDate: null,
    endDate: null,
    appointments: [],
    showAllClinics: false,
};

/*
|--------------------------------------------------------------------------
| Getters
|--------------------------------------------------------------------------
*/

const getters = {
    filteredAppointments(state) {
        let prevTime;

        return JSON.parse(JSON.stringify(
            state.appointments.filter(clinic => {
                return clinic.Distance <= 25 || clinic.ClinicID == process.env.VUE_APP_VIDEO_CLINIC_ID;
            }).map(clinic => {
                clinic.Appointments = clinic.Appointments.filter(appt => {
                    const isSameTime = prevTime === appt.Time;
                    prevTime = appt.Time;
                    return !isSameTime;
                });

                return clinic;
            })
        ));
    },

    filteredAndOrderedAppointments(state, getters, rootState) {
        let clinics = getters.filteredAppointments;

        if (rootState['appointment'].sort === 'soonest') {
            clinics = clinics.sort((a, b) => {
                const earliestA = getEarliestAppointment(a);
                const earliestB = getEarliestAppointment(b);

                if (!earliestA && earliestB) {
                    return 1;
                } else if (!earliestB && earliestA) {
                    return -1;
                } else if (!earliestA && !earliestB) {
                    return 0;
                }

                if (earliestA.isAfter(earliestB)) {
                    return 1;
                } else if (earliestA.isBefore(earliestB)) {
                    return -1;
                }

                return a.Distance > b.Distance ? 1 : a.Distance < b.Distance ? -1 : 0;
            });
        } else {
            clinics = clinics.sort((a, b) => {
                return a.Distance > b.Distance ? 1 : a.Distance < b.Distance ? -1 : 0;
            });
        }

        return clinics.filter((clinic, index) => {
            return state.showAllClinics || index <= 4;
        });
    },

    availableClinicians: () => (clinic) => {
        let clinicians = [];

        window._.forEach(clinic.Appointments, appointment => {
            const alreadyAdded = clinicians.filter(clinician => clinician.TherapistID === appointment.Therapist.TherapistID).length;

            if (!alreadyAdded) {
                clinicians.push(appointment.Therapist);
            }
        });

        return window._.orderBy(clinicians, ['Name']);
    },
};

/*
|--------------------------------------------------------------------------
| Actions
|--------------------------------------------------------------------------
*/

const actions = {
    toggleShowAllClinics({ commit }) {
        commit('toggleShowAllClinics');
    },

    getAppointments({ commit, rootState }, { callback, params }) {
        // If the selected start date does not allow us to query 5 days,
        // we'll update the date in the store.
        let startDateAltered = false;

        commit('setAppointmentsLoading', true);

        const successCallback = (response) => {
            const { startDate, endDate } = getStartAndEndDates(response.data.data);

            commit('setAppointments', response.data.data);
            commit('setStartDate', startDate);
            commit('setEndDate', endDate);

            if (!startDateAltered && params && params.startDate) {
                commit('appointment/setDate', params.startDate, { root: true });
            }

            commit('setAppointmentsLoading', false);

            window.Events.dispatch('appointments.load');

            callback && callback();
        };

        const errorCallback = () => {
            commit('setAppointmentsLoading', false);
        };

        const maxDate = window.moment().startOf('day').add(process.env.VUE_APP_MAX_DAYS, 'days');

        let startDate, endDate;

        if (params && params.startDate) {
            startDate = params.startDate;
        } else if (rootState['appointment'].date) {
            startDate = rootState['appointment'].date;
        } else {
            startDate = window.moment().startOf('day');
        }

        if (startDate.isAfter(maxDate)) {
            startDate = maxDate;
        }

        // We'll query 6 days instead of 5 so that we can easily hide Sundays.
        endDate = window.moment(startDate).add(5, 'days');

        if (endDate.isAfter(maxDate)) {
            endDate = maxDate;
            startDate = window.moment(endDate).subtract(4, 'day');

            // Subtract another day if we need to hide Sunday.
            if (DateTime.includesSunday(startDate)) {
                startDate.subtract(1, 'day');
            }

            commit('appointment/setDate', startDate, { root: true });
            startDateAltered = true;
        }

        const place = rootState['appointment'].place;        
        const queryString = window.queryString.stringify({
            ClinicId: rootState['appointment'].clinicId ? [rootState['appointment'].clinicId] : null,
            StartDate: startDate.format('MM/DD/YYYY'),
            EndDate: endDate.format('MM/DD/YYYY'),
            Latitude: place ? place.geometry.location.lat : 0,
            Longitude: place ? place.geometry.location.lng : 0,
        }, {
            arrayFormat: 'bracket',
        });

        window.axios.get('v1/appointments?' + queryString).then(successCallback).catch(errorCallback);
    },

    getNextAvailableAppointments({ commit, dispatch, rootState }, { clinic, callback }) {
        dispatch('appointment/setClinicId', clinic, { root: true });

        commit('setAppointmentsLoading', true);

        const successCallback = (response) => {
            // If there are no appointments, trigger an event so we can alert the user.
            if (!response.data.data.length) {
                window.Events.dispatch('appointments.next.empty');

                commit('setAppointmentsLoading', false);
            } else {
                const { startDate, endDate } = getStartAndEndDates(response.data.data);

                commit('setAppointments', response.data.data);
                commit('setStartDate', startDate);
                commit('setEndDate', endDate);

                commit('appointment/setDate', startDate, { root: true });

                commit('setAppointmentsLoading', false);

                window.Events.dispatch('appointments.load');
            }

            callback && callback(response.data.data);
        };

        const errorCallback = () => {
            commit('setAppointmentsLoading', false);

            callback && callback();
        };

        const place = rootState['appointment'].place;
        const queryString = window.queryString.stringify({
            ClinicID: clinic.ClinicID,
            Latitude: place ? place.geometry.location.lat : 0,
            Longitude: place ? place.geometry.location.lng : 0,
        });

        window.axios.get('v1/appointments/next?' + queryString).then(successCallback).catch(errorCallback);
    },

    setReservedAppointment({ commit }, { clinic, appointment }) {
        commit('setReservedAppointment', {
            clinic,
            therapist: appointment.Therapist,
            date: appointment.Date,
            time: appointment.Time,
            length: appointment.Length,
        });
    },

    clearReservedAppointment({ commit }) {
        commit('setReservedAppointment', null);
    },

    reserveAppointment({ commit, dispatch }, { callback, params }) {
        commit('setReservingAppointment', true);

        const successCallback = (response) => {
            if (response.data.data.AppointmentID === null) {
                dispatch('appointment/setAppointmentData', null, { root: true });

                commit('setReservingAppointment', false);

                window.Events.dispatch('appointment.reserve.error', {
                    message: response.data.data.Message,
                });
            } else {
                params.appointment.AppointmentID = response.data.data.AppointmentID;

                // Appointments are reserved for 15 minutes, but we'll give ourselves 60 seconds
                // of breathing room so that the Laravel API can process a payment in time without
                // running the risk of the slot becoming unavailable after taking payment.
                params.expires = window.moment().add(15, 'minute');

                dispatch('appointment/setAppointmentData', params, { root: true });

                commit('setReservingAppointment', false);

                callback && callback(response.data.data);
            }
        };

        const errorCallback = () => {
            commit('setReservingAppointment', false);
        };

        const requestParams = {
            ClinicID: params.clinic.ClinicID,
            TherapistID: params.therapist.TherapistID,
            Date: window.moment(params.appointment.Date).format('MM/DD/YYYY'),
            Time: params.appointment.Time.substr(0, params.appointment.Time.length -3),
            AppointmentLength: params.appointment.AppointmentLength,
        };

        window.axios.post('v1/appointment/reserve', requestParams).then(successCallback).catch(errorCallback);
    },

    cancelReservedAppointment({ commit, dispatch }, { appointmentId, callback }) {
        commit('setCancellingReservedAppointment', true);

        const successCallback = () => {
            dispatch('appointment/setAppointmentData', null, { root: true });

            commit('setCancellingReservedAppointment', false);

            callback && callback();
        };

        const errorCallback = () => {
            commit('setCancellingReservedAppointment', false);
        };

        const requestParams = {
            AppointmentID: appointmentId,
        };

        window.axios.post('v1/appointment/cancel-reservation', requestParams).then(successCallback).catch(errorCallback);
    }
};

/*
|--------------------------------------------------------------------------
| Mutations
|--------------------------------------------------------------------------
*/

const mutations = {
    toggleShowAllClinics(state) {
        state.showAllClinics = !state.showAllClinics;
    },

    setAppointmentsLoading(state, loading) {
        state.loadingAppointments = loading;
    },

    setReservedAppointment(state, data) {
        state.reservedAppointment = data;
    },

    setReservingAppointment(state, loading) {
        state.reservingAppointment = loading;
    },

    setCancellingReservedAppointment(state, loading) {
        state.cancellingReservedAppointment = loading;
    },

    setAppointments(state, appointments) {
        state.appointments = appointments;
    },

    setStartDate(state, startDate) {
        state.startDate = startDate;
    },

    setEndDate(state, endDate) {
        state.endDate = endDate;
    },
};

/*
|--------------------------------------------------------------------------
| Export
|--------------------------------------------------------------------------
*/

export const appointments = {
    namespaced: true,
    actions,
    getters,
    mutations,
    state,
};
